import React from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Paper,
  useTheme,
  alpha,
  AppBar,
  Toolbar,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { useFormik } from "formik";
import { useCookies } from 'react-cookie';
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { login } from "../../services/request/Auth";
import { BackdropComponent } from "../../shared-components/backdrop/Backdrop";
import { ADMIN_DASHBOARD, ADMIN_LEADS, LEADS_LIST, SIGN_UP } from "../../services/constants/URIConstants";
import { RocketLaunch, AdminPanelSettings, LockOpen } from "@mui/icons-material";
import { usePriceMasking } from "../../contexts/PriceMaskingContext";

const Login = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [cookies, setCookie] = useCookies(['auth_token']);
  const [open, setOpen] = React.useState(false);
  const { isPriceMasked, togglePriceMasking } = usePriceMasking();
  const formik = useFormik({
    initialValues: {
      email: "",
      pin: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      pin: Yup.string()
        .required("Password is required")
        .min(8, "Password must be at least 8 characters long")
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
          "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character (@$!%*?&)"
        ),
    }),
    onSubmit: (values, { resetForm }) => {
      setOpen(true);        
      login(formik.values)
        .then((result) => {
          console.log("Result ", result);
          //   setReleaseTopics(releaseTopics.data);
          setOpen(false);          
          if(result.status===200 && result.data.loginSuccess){
            
            setCookie(`auth_token`, result.data.token, { path: '/' });
            toast.success("Login successful!", { position: "bottom-center" });
            navigate(ADMIN_DASHBOARD);
          }else if(result.status===403){              
            toast.error("Invlid Login", { position: "bottom-center" });
          }
        
        })
        .catch((err) => {
          setOpen(false);
          toast.error("Login Failed!", { position: "bottom-center" });
        });
      console.log("Login Data:", values);
      resetForm();
    },
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #00f2fe 100%)',
        backgroundSize: '400% 400%',
        animation: 'gradientShift 15s ease infinite',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
        '@keyframes gradientShift': {
          '0%': { backgroundPosition: '0% 50%' },
          '50%': { backgroundPosition: '100% 50%' },
          '100%': { backgroundPosition: '0% 50%' },
        },
      }}
    >
      {/* Floating Tech Elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          pointerEvents: 'none',
          '& > *': {
            position: 'absolute',
            opacity: 0.1,
            animation: 'float 20s infinite ease-in-out',
          },
        }}
      >
        {[...Array(6)].map((_, i) => (
          <Box
            key={i}
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.3)}, ${alpha(theme.palette.secondary.main, 0.3)})`,
              left: `${(i * 16) % 100}%`,
              top: `${(i * 20) % 100}%`,
              animationDelay: `${i * 2}s`,
              '@keyframes float': {
                '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
                '50%': { transform: 'translateY(-50px) rotate(180deg)' },
              },
            }}
          />
        ))}
      </Box>

      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 20px rgba(0, 0, 0, 0.1)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
            }}
            onClick={() => navigate('/')}
          >
            <RocketLaunch sx={{ color: 'white' }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'white',
              }}
            >
              Whatzneu
            </Typography>
          </Box>
          <Button
            variant="text"
            onClick={() => navigate('/')}
            sx={{
              color: 'white',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Back to Home
          </Button>
        </Toolbar>
      </AppBar>

      <BackdropComponent visible={open} />
      <ToastContainer />

      <Container 
        maxWidth="sm" 
        sx={{ 
          position: 'relative', 
          zIndex: 1,
          pt: { xs: 12, md: 14 },
          pb: 6,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            padding: { xs: 3, md: 4 },
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                display: 'inline-flex',
                p: 2,
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                mb: 2,
              }}
            >
              <LockOpen sx={{ fontSize: 48, color: 'white' }} />
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: 'white',
                mb: 1,
                fontSize: { xs: '1.8rem', md: '2.2rem' },
              }}
            >
              Admin Login
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: alpha(theme.palette.common.white, 0.9),
                fontSize: { xs: '0.9rem', md: '1rem' },
              }}
            >
              Access your Whatzneu admin dashboard
            </Typography>
          </Box>

          <Box component="form" onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              margin="normal"
              type="email"
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: 2,
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.8)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'rgba(255, 255, 255, 1)',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: alpha(theme.palette.common.white, 0.9),
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'white',
                },
              }}
            />
            <TextField
              fullWidth
              label="Password"
              name="pin"
              value={formik.values.pin}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              margin="normal"
              type="password"
              error={formik.touched.pin && Boolean(formik.errors.pin)}
              helperText={formik.touched.pin && formik.errors.pin}
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: 2,
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.8)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'rgba(255, 255, 255, 1)',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: alpha(theme.palette.common.white, 0.9),
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'white',
                },
                '& .MuiFormHelperText-root': {
                  color: alpha(theme.palette.common.white, 0.8),
                },
              }}
            />

            <Box display="flex" justifyContent="space-between" mt={4} gap={2}>
              <Button
                variant="outlined"
                onClick={() => formik.resetForm()}
                sx={{
                  flex: 1,
                  py: 1.5,
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  color: 'white',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: '1px solid rgba(255, 255, 255, 0.6)',
                  },
                }}
              >
                Clear
              </Button>
              <Button
                variant="contained"
                type="submit"
                startIcon={<AdminPanelSettings />}
                sx={{
                  flex: 1,
                  py: 1.5,
                  background: 'rgba(255, 255, 255, 0.25)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  color: 'white',
                  fontWeight: 700,
                  textTransform: 'none',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.35)',
                    border: '1px solid rgba(255, 255, 255, 0.6)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Login
              </Button>
            </Box>

            {/* Price Masking Toggle */}
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={isPriceMasked}
                    onChange={togglePriceMasking}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: 'white',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'white',
                      fontWeight: 500,
                    }}
                  >
                    {isPriceMasked ? 'Prices are masked' : 'Prices are visible'}
                  </Typography>
                }
                sx={{
                  m: 0,
                  width: '100%',
                  justifyContent: 'space-between',
                }}
              />
            </Box>
            
            {/* Signup button - only visible in development */}
            {process.env.NODE_ENV !== 'production' && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Button
                  variant="text"
                  onClick={() => navigate(SIGN_UP)}
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    textTransform: 'none',
                    py: 1,
                    px: 3,
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: 2,
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.2)',
                      border: '1px solid rgba(255, 255, 255, 0.5)',
                    },
                  }}
                >
                  Don't have an account? Sign Up
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
